// 📁 components/categoria/JuntaDirectivaView.js - IMPORTS CORREGIDOS
import React from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Avatar,
  Chip
} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupsIcon from "@mui/icons-material/Groups";
import { theme } from "../../styles/CategoriaStyles";
import { RepresentanteCard } from "./RepresentanteItem";
import { 
  ordenarPorJerarquiaConVocales,
  obtenerCargoJunta 
} from "../../utils/representanteUtils";
import { 
  getNombreDepartamento,
  getMiembrosJuntaDirectiva  // ← IMPORT CORREGIDO: viene de categoriaHelpers
} from "../../utils/categoriaHelpers";

// Componente Organigrama
const OrganigramaJuntaDirectiva = ({ representantes, asociacion }) => {
  const miembros = getMiembrosJuntaDirectiva(representantes, asociacion?.cod_asociacion);
  
  if (!miembros.todos?.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center", backgroundColor: "#FFF" }}>
        <GroupsIcon sx={{ fontSize: 60, color: theme.palette.border.light, mb: 2 }} />
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
          No hay miembros en la Junta Directiva para {asociacion?.nombre_abreviado}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 3, md: 6 }, 
      backgroundColor: "#FFF", 
      minHeight: "800px", 
      maxWidth: "1400px", 
      margin: "0 auto" 
    }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <AccountTreeIcon sx={{ 
          fontSize: { xs: 36, md: 48 }, 
          color: theme.palette.primary.main, 
          mb: 1 
        }} />
        <Typography variant="h4" sx={{ 
          color: theme.palette.primary.main, 
          fontWeight: 700, 
          mb: 1,
          fontSize: { xs: "1.5rem", md: "2rem" }
        }}>
          JUNTA DIRECTIVA
        </Typography>
        <Typography variant="h6" sx={{ 
          fontSize: { xs: "1rem", md: "1.25rem" } 
        }}>
          {asociacion?.nombre_abreviado || "Junta Directiva"}
        </Typography>
      </Box>

      {/* Presidente */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <RepresentanteCard 
          representante={miembros.presidente} 
          cargoLabel="PRESIDENTE" 
          isMain={true} 
        />
      </Box>

      {/* Línea de conexión */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ 
          width: "4px", 
          height: "50px", 
          backgroundColor: theme.palette.primary.main 
        }} />
      </Box>

      {/* Vicepresidente */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <RepresentanteCard 
          representante={miembros.vicepresidente} 
          cargoLabel="VICEPRESIDENTE" 
        />
      </Box>

      {/* Línea de conexión */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ 
          width: "4px", 
          height: "40px", 
          backgroundColor: theme.palette.primary.main 
        }} />
      </Box>

      {/* Tesorero y Secretario */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        gap: { xs: 4, md: 8 }, 
        mb: 4, 
        flexWrap: "wrap" 
      }}>
        <RepresentanteCard 
          representante={miembros.tesorero} 
          cargoLabel="TESORERO" 
        />
        <RepresentanteCard 
          representante={miembros.secretario} 
          cargoLabel="SECRETARIO" 
        />
      </Box>

      {/* Vocales */}
      {miembros.vocales.length > 0 && (
        <>
          <Typography variant="h5" sx={{ 
            textAlign: "center", 
            mb: 3, 
            color: theme.palette.accent.main, 
            fontWeight: 700,
            fontSize: { xs: "1.2rem", md: "1.5rem" }
          }}>
            VOCALES (Ordenados I-X)
          </Typography>
          
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            gap: 2, 
            flexWrap: "wrap", 
            mb: 4 
          }}>
            {miembros.vocales.map((vocal, index) => (
              <RepresentanteCard 
                key={vocal.cod_representante || index} 
                representante={vocal} 
                cargoLabel={obtenerCargoJunta(vocal)}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// Componente Lista
const ListaJuntaDirectiva = ({ representantes, asociacion, departamentos }) => {
  const representantesFiltrados = representantes?.filter(rep => {
    const perteneceAsociacion = rep.cod_asociacion === asociacion?.cod_asociacion;
    const tieneJuntaDirectiva = rep.categorias?.some(cat => cat.Id === 4 && cat.Estado !== 'Inactivo');
    return perteneceAsociacion && tieneJuntaDirectiva;
  }) || [];

  const representantesOrdenados = ordenarPorJerarquiaConVocales(representantesFiltrados);

  if (!representantesFiltrados.length) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <GroupsIcon sx={{ fontSize: 60, color: theme.palette.border.light, mb: 2 }} />
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
          No hay miembros en la Junta Directiva para {asociacion?.nombre_abreviado}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" sx={{ 
        mb: 3, 
        color: theme.palette.primary.main, 
        fontWeight: 700,
        display: "flex", 
        alignItems: "center", 
        gap: 1, 
        px: 3, 
        pt: 3,
        fontSize: { xs: "1.2rem", md: "1.5rem" }
      }}>
        <ListAltIcon />
        Lista de Junta Directiva - {asociacion?.nombre_abreviado}
        <Typography variant="caption" sx={{ 
          ml: 2, 
          color: theme.palette.text.secondary 
        }}>
          ({representantesOrdenados.length} miembros)
        </Typography>
      </Typography>

      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          border: `1px solid ${theme.palette.border.light}`,
          borderRadius: "12px", 
          mx: 3, 
          mb: 3, 
          overflowX: "auto"
        }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
            <TableRow>
              <TableCell sx={{ 
                color: theme.palette.primary.contrastText, 
                fontWeight: 700, 
                textAlign: "center", 
                py: 2 
              }}>
                Nombre Completo
              </TableCell>
              <TableCell sx={{ 
                color: theme.palette.primary.contrastText, 
                fontWeight: 700, 
                textAlign: "center", 
                py: 2 
              }}>
                Cargo
              </TableCell>
              <TableCell sx={{ 
                color: theme.palette.primary.contrastText, 
                fontWeight: 700, 
                textAlign: "center", 
                py: 2, 
                display: { xs: "none", sm: "table-cell" } 
              }}>
                DNI
              </TableCell>
              <TableCell sx={{ 
                color: theme.palette.primary.contrastText, 
                fontWeight: 700, 
                textAlign: "center", 
                py: 2, 
                display: { xs: "none", lg: "table-cell" } 
              }}>
                Departamento
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {representantesOrdenados.map((representante) => {
              const categoriaJunta = representante.categorias?.find(cat => cat.Id === 4 && cat.Estado !== 'Inactivo');
              const cargo = categoriaJunta?.Cargo || "Miembro";
              
              return (
                <TableRow 
                  key={representante.cod_representante} 
                  sx={{
                    "&:nth-of-type(odd)": { 
                      backgroundColor: theme.palette.background.paper 
                    },
                    "&:hover": { 
                      backgroundColor: "rgba(139, 90, 43, 0.05)" 
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, textAlign: "center", py: 2 }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      justifyContent: "center" 
                    }}>
                      <Avatar 
                        src={representante.fotografia || ""} 
                        sx={{ 
                          width: 30, 
                          height: 30, 
                          border: `2px solid ${theme.palette.primary.main}` 
                        }}
                      >
                        {representante.nombre_representante?.charAt(0) || "R"}
                      </Avatar>
                      {representante.nombre_representante || "Sin nombre"}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", py: 2 }}>
                    <Chip 
                      label={cargo} 
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText, 
                        fontWeight: 600
                      }} 
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    textAlign: "center", 
                    py: 2, 
                    display: { xs: "none", sm: "table-cell" } 
                  }}>
                    {representante.dni || "Sin DNI"}
                  </TableCell>
                  <TableCell sx={{ 
                    textAlign: "center", 
                    py: 2, 
                    display: { xs: "none", lg: "table-cell" } 
                  }}>
                    {getNombreDepartamento(departamentos, representante.cod_depto)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Componente Principal
const JuntaDirectivaView = ({ 
  representantes, 
  asociacion, 
  departamentos, 
  vistaJuntaDirectiva, 
  onVistaChange 
}) => {
  return (
    <Box sx={{ p: 3 }}>
      {/* Toggle de Vista */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <ToggleButtonGroup 
          value={vistaJuntaDirectiva} 
          exclusive 
          onChange={onVistaChange} 
          sx={{ 
            backgroundColor: theme.palette.background.card, 
            borderRadius: "12px" 
          }}
        >
          <ToggleButton value="lista">
            <ListAltIcon sx={{ mr: 1 }} />
            Lista
          </ToggleButton>
          <ToggleButton value="organigrama">
            <AccountTreeIcon sx={{ mr: 1 }} />
            Organigrama
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Contenido según vista */}
      {vistaJuntaDirectiva === 'organigrama' ? (
        <OrganigramaJuntaDirectiva 
          representantes={representantes} 
          asociacion={asociacion} 
        />
      ) : (
        <ListaJuntaDirectiva 
          representantes={representantes} 
          asociacion={asociacion} 
          departamentos={departamentos} 
        />
      )}
    </Box>
  );
};

export default JuntaDirectivaView;